import React, { useEffect, useState } from "react";
import {
  Beer, UtensilsCrossed, Coffee, ExternalLink, Loader2, MapPin,
  ShoppingBasket, ShoppingCart, Croissant, Fuel, Pill, Banknote, Mail,
  Landmark, Store,
} from "lucide-react";
import { api } from "../../lib/api";
import { matchBrandLogo } from "../../lib/brandLogos";

const TYPE_META = {
  pub:          { icon: Beer,             label: "Pub",           tint: "text-amber-700",   bg: "bg-amber-50" },
  bar:          { icon: Beer,             label: "Bar",           tint: "text-amber-700",   bg: "bg-amber-50" },
  restaurant:   { icon: UtensilsCrossed,  label: "Restaurant",    tint: "text-rose-700",    bg: "bg-rose-50" },
  cafe:         { icon: Coffee,           label: "Cafe",          tint: "text-stone-700",   bg: "bg-stone-50" },
  fast_food:    { icon: UtensilsCrossed,  label: "Takeaway",      tint: "text-orange-700",  bg: "bg-orange-50" },
  supermarket:  { icon: ShoppingCart,     label: "Supermarket",   tint: "text-emerald-700", bg: "bg-emerald-50" },
  convenience:  { icon: ShoppingBasket,   label: "Convenience",   tint: "text-emerald-700", bg: "bg-emerald-50" },
  bakery:       { icon: Croissant,        label: "Bakery",        tint: "text-yellow-800",  bg: "bg-yellow-50" },
  food_shop:    { icon: ShoppingBasket,   label: "Food shop",     tint: "text-emerald-700", bg: "bg-emerald-50" },
  shop:         { icon: Store,            label: "Shop",          tint: "text-indigo-700",  bg: "bg-indigo-50" },
  fuel:         { icon: Fuel,             label: "Petrol",        tint: "text-slate-700",   bg: "bg-slate-100" },
  pharmacy:     { icon: Pill,             label: "Pharmacy",      tint: "text-teal-700",    bg: "bg-teal-50" },
  atm:          { icon: Banknote,         label: "ATM",           tint: "text-lime-800",    bg: "bg-lime-50" },
  bank:         { icon: Landmark,         label: "Bank",          tint: "text-blue-800",    bg: "bg-blue-50" },
  post_office:  { icon: Mail,             label: "Post office",   tint: "text-red-700",     bg: "bg-red-50" },
  attraction:   { icon: MapPin,           label: "Attraction",    tint: "text-fuchsia-700", bg: "bg-fuchsia-50" },
  place:        { icon: Store,            label: "Nearby",        tint: "text-neutral-700", bg: "bg-neutral-50" },
};

/**
 * BrandOrIcon — renders the matched chain's real logo when available,
 * falling back to the type icon on 404 / block / unmatched name. The
 * fallback swap is instant (state flip on `onError`) so failed image
 * requests never leave a broken image glyph on screen.
 */
function BrandOrIcon({ brand, Icon, meta }) {
  const [failed, setFailed] = React.useState(false);
  if (brand && !failed) {
    return (
      <div
        className="w-10 h-10 flex items-center justify-center bg-white border hairline shrink-0 overflow-hidden"
        data-testid="nearby-brand-logo"
        title={brand.brand}
      >
        <img
          src={brand.logoUrl}
          alt={`${brand.brand} logo`}
          className="w-full h-full object-contain p-1"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }
  return (
    <div className={`w-10 h-10 flex items-center justify-center ${meta.bg} ${meta.tint} shrink-0`}>
      <Icon className="w-5 h-5" strokeWidth={1.75} />
    </div>
  );
}

export default function NearbyAmenities({ campsiteId }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    api.get(`/campsites/${campsiteId}/nearby`)
      .then(({ data }) => { if (alive) setResults(data.results || []); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Could not load nearby places"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [campsiteId]);

  return (
    <section className="mt-12" data-testid="nearby-section">
      <div className="eyebrow mb-3">Nearby</div>
      <h2 className="font-display text-2xl mb-5">Pubs, shops & services within 5 km</h2>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#555] py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Looking around the area…
        </div>
      )}

      {!loading && err && (
        <div className="p-4 border hairline text-sm text-[#555]">
          We couldn't fetch nearby places right now. Try refreshing in a moment.
        </div>
      )}

      {!loading && !err && results && results.length === 0 && (
        <div className="p-5 border hairline bg-[#fafafa]" data-testid="nearby-empty">
          <p className="text-sm text-[#333] leading-relaxed">
            <strong>This site is properly off-grid</strong> — no shops, pubs, cafes or services within
            5 km on our records. Pack a flask, a stove and your favourite supper. That&apos;s half the
            magic.
          </p>
        </div>
      )}

      {!loading && !err && results && results.length > 0 && (
        <ul className="border hairline bg-white divide-y divide-[#eee]" data-testid="nearby-list">
          {results.map((r, i) => {
            const meta = TYPE_META[r.type] || TYPE_META.restaurant;
            const Icon = meta.icon;
            const brand = matchBrandLogo(r.name);
            return (
              <li key={`${r.name}-${i}`} className="p-4 flex items-center gap-4" data-testid={`nearby-item-${i}`}>
                <BrandOrIcon brand={brand} Icon={Icon} meta={meta} />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base leading-tight truncate">{r.name}</div>
                  <div className="text-xs mono text-[#666] mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="uppercase tracking-wider">{meta.label}</span>
                    <span><MapPin className="inline w-3 h-3 -mt-0.5" /> {r.distance_km.toFixed(1)} km</span>
                    <span>~{r.walk_min} min walk</span>
                    {r.cuisine && <span className="text-[#888] capitalize">{r.cuisine.replace(/_/g," ")}</span>}
                  </div>
                </div>
                <a href={r.directions_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#ff4500] hover:underline shrink-0"
                  data-testid={`nearby-directions-${i}`}>
                  Directions <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-[11px] text-[#888] mt-3">
        Data from OpenStreetMap · distances "as the crow flies" · walk times approximate
      </p>
    </section>
  );
}
